import { supabase } from '../supabaseClient';
import { buildListHeadVersion } from '../utils/platformGlobalAdminCache';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  AnnualProjectProgram,
  cacheAnnualProjectPrograms,
  getAnnualProjectPrograms as getAnnualProjectProgramsFromStorage,
  getAnnualProjectProgramsCacheWriteGeneration,
  isAnnualProjectProgramsSnapshotUsable,
  loadAnnualProjectPrograms,
  readAnnualProjectProgramsCache,
  readAnnualProjectProgramsCacheSnapshot,
  type GetAnnualProjectProgramsOptions,
} from '../utils/annualProjectProgramsStorage';
import { invalidateSchoolScopedDataCaches } from '../utils/schoolDataCacheVersion';

const ANNUAL_PROJECT_PROGRAMS_SELECT =
  'id, workspace_id, grade_id, academic_year, term_number, name, color_hex, created_at, updated_at';

type AnnualProjectProgramRecord = {
  id: string;
  workspace_id: string;
  grade_id: string;
  academic_year: number;
  term_number: 1 | 2;
  name: string;
  color_hex: string;
  updated_at?: string | null;
};

function toPrograms(records: AnnualProjectProgramRecord[]): AnnualProjectProgram[] {
  return records.map((row) => ({
    id: row.id,
    name: row.name,
    colorHex: row.color_hex,
    termNumber: row.term_number,
  }));
}

function buildProgramsHeadVersion(records: Array<{ id: string; updated_at?: string | null }>): string {
  return buildListHeadVersion(
    records.map((r) => ({
      id: String(r.id),
      updated_at: typeof r.updated_at === 'string' ? r.updated_at : '',
    }))
  );
}

/** storage 구현과 동일: LS 즉시 표시 + head 비교 후 필요 시 풀 fetch */
export async function getAnnualProjectPrograms(
  workspaceId: string,
  gradeId: string,
  academicYear: number,
  options?: GetAnnualProjectProgramsOptions
): Promise<AnnualProjectProgram[]> {
  return getAnnualProjectProgramsFromStorage(workspaceId, gradeId, academicYear, options);
}

export async function getAnnualProjectProgramsMap(
  workspaceId: string,
  academicYear: number,
  gradeIds: string[],
  options?: GetAnnualProjectProgramsOptions
): Promise<Record<string, AnnualProjectProgram[]>> {
  const uniqueGradeIds = Array.from(new Set(gradeIds.filter(Boolean)));
  if (!workspaceId || uniqueGradeIds.length === 0) return {};

  const cacheOnly = (): Record<string, AnnualProjectProgram[]> => {
    const out: Record<string, AnnualProjectProgram[]> = {};
    uniqueGradeIds.forEach((gradeId) => {
      out[gradeId] = readAnnualProjectProgramsCache(workspaceId, gradeId, academicYear);
    });
    return out;
  };

  if (!options?.forceRefresh) {
    const snapshots = uniqueGradeIds.map((gradeId) => ({
      gradeId,
      snap: readAnnualProjectProgramsCacheSnapshot(workspaceId, gradeId, academicYear),
    }));
    /** 서버와 맞춘 캐시(stale 아님)면 네트워크 없이 사용 — 원격 변경은 로그인·새로고침 파트 비교가 stale 표시 */
    if (snapshots.every(({ snap }) => isAnnualProjectProgramsSnapshotUsable(snap))) {
      if (process.env.NODE_ENV === 'development') {
        recordDevLocalStorageProgramCacheUse('연간프로젝트맵·LS');
      }
      return cacheOnly();
    }
  }

  const writeGenAtFetchStart = getAnnualProjectProgramsCacheWriteGeneration();
  try {
    const { data, error } = await supabase
      .from('annual_project_programs')
      .select(ANNUAL_PROJECT_PROGRAMS_SELECT)
      .eq('workspace_id', workspaceId)
      .eq('academic_year', academicYear)
      .in('grade_id', uniqueGradeIds)
      .order('grade_id', { ascending: true })
      .order('term_number', { ascending: true })
      .order('name', { ascending: true });
    if (error) return cacheOnly();
    const grouped: Record<string, AnnualProjectProgramRecord[]> = {};
    uniqueGradeIds.forEach((gradeId) => {
      grouped[gradeId] = [];
    });
    ((data ?? []) as AnnualProjectProgramRecord[]).forEach((row) => {
      if (!grouped[row.grade_id]) grouped[row.grade_id] = [];
      grouped[row.grade_id].push(row);
    });
    const out: Record<string, AnnualProjectProgram[]> = {};
    if (getAnnualProjectProgramsCacheWriteGeneration() !== writeGenAtFetchStart) {
      return cacheOnly();
    }
    for (const gradeId of uniqueGradeIds) {
      const records = grouped[gradeId] ?? [];
      const programs = toPrograms(records);
      const snap = readAnnualProjectProgramsCacheSnapshot(workspaceId, gradeId, academicYear);
      if (programs.length === 0 && snap.programs.length > 0 && snap.version == null) {
        try {
          await replaceAnnualProjectPrograms(workspaceId, gradeId, academicYear, snap.programs);
        } catch {
          /* 오프라인 등 */
        }
        out[gradeId] = snap.programs;
        continue;
      }
      const version = buildProgramsHeadVersion(records);
      cacheAnnualProjectPrograms(workspaceId, gradeId, academicYear, programs, { force: true, version });
      out[gradeId] = programs;
    }
    return out;
  } catch {
    return cacheOnly();
  }
}

export async function replaceAnnualProjectPrograms(
  workspaceId: string,
  gradeId: string,
  academicYear: number,
  programs: AnnualProjectProgram[]
): Promise<AnnualProjectProgram[]> {
  if (!workspaceId || !gradeId) return [];
  const savedAt = new Date().toISOString();
  const normalized = programs.map((program) => ({
    id: program.id.trim(),
    workspace_id: workspaceId,
    grade_id: gradeId,
    academic_year: academicYear,
    term_number: program.termNumber,
    name: program.name.trim(),
    color_hex: program.colorHex.trim(),
    updated_at: savedAt,
  }));
  const keepIds = new Set(normalized.map((row) => row.id).filter(Boolean));
  const { data: existingRows, error: existingError } = await supabase
    .from('annual_project_programs')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('academic_year', academicYear);
  if (existingError) throw new Error(existingError.message);
  const removeIds = ((existingRows ?? []) as Array<{ id: string }>).map((row) => row.id).filter((id) => !keepIds.has(id));
  if (removeIds.length > 0) {
    const { error: deleteError } = await supabase.from('annual_project_programs').delete().in('id', removeIds);
    if (deleteError) throw new Error(deleteError.message);
  }
  if (normalized.length > 0) {
    const { error: upsertError } = await supabase
      .from('annual_project_programs')
      .upsert(normalized, { onConflict: 'id' });
    if (upsertError) throw new Error(upsertError.message);
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['annualProjects']);
  const version = buildProgramsHeadVersion(normalized);
  cacheAnnualProjectPrograms(workspaceId, gradeId, academicYear, programs, { force: true, version });
  return programs;
}

export function getCachedAnnualProjectPrograms(
  workspaceId: string,
  gradeId: string,
  academicYear: number
): AnnualProjectProgram[] {
  return loadAnnualProjectPrograms(workspaceId, gradeId, academicYear);
}
